// ./src/components/features/Connectors/ConfigEditors/FtpConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Alert,
  NumberInput,
  Switch,
  Group,
} from "@mantine/core";
import { IconInfoCircle, IconLock } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { FtpConnectorConfig } from "@/types";

export interface FtpConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;

  /** Whether this is FTPS (shows TLS options) - internal use */
  isSecure?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: FtpConnectorConfig = {
  host: "",
  port: 21,
  username: "",
  password: "",
  pathPrefix: "/",
  fileNamePattern: "{{requestId}}.json",
  rejectUnauthorized: true,
  secureImplicit: false,
};

/**
 * Parse and validate FTP connector configuration
 */
const parseFtpConfig = (jsonString: string): FtpConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

/**
 * Build example path based on current config
 */
const buildExamplePath = (config: FtpConnectorConfig): string => {
  const prefix = config.pathPrefix || "/";
  const normalizedPrefix = prefix.endsWith("/") ? prefix : `${prefix}/`;

  // Replace placeholders with example values
  let examplePrefix = normalizedPrefix
    .replace(/\{\{formName\}\}/g, "contact-form")
    .replace(/\{\{formId\}\}/g, "abc123")
    .replace(/\{\{date\}\}/g, "2025-01-15")
    .replace(/\{\{year\}\}/g, "2025")
    .replace(/\{\{month\}\}/g, "01")
    .replace(/\{\{day\}\}/g, "15");

  return `${examplePrefix}20250115143022-ABCD-resume.pdf`;
};

/**
 * Base FTP/FTPS configuration editor component
 */
const FtpConnectorConfigEditorBase: React.FC<FtpConnectorConfigEditorProps> = ({
  value,
  onChange,
  disabled = false,
  isSecure = false,
}) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<FtpConnectorConfig>(() =>
    parseFtpConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseFtpConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: FtpConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleHostChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, host: e.target.value.trim() });
  };

  const handlePortChange = (value: string | number) => {
    const port = typeof value === "string" ? parseInt(value, 10) : value;
    updateConfig({ ...config, port: isNaN(port) ? 21 : port });
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, username: e.target.value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, password: e.target.value });
  };

  const handlePathPrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, pathPrefix: e.target.value });
  };

  const handleFileNamePatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, fileNamePattern: e.target.value });
  };

  const handleRejectUnauthorizedChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, rejectUnauthorized: e.target.checked });
  };

  const handleSecureImplicitChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // If switching to implicit mode, suggest port 990
    const newPort = e.target.checked && config.port === 21 ? 990 : config.port;
    updateConfig({
      ...config,
      secureImplicit: e.target.checked,
      port: newPort,
    });
  };

  const protocol = isSecure ? "FTPS" : "FTP";

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert
        icon={isSecure ? <IconLock size={16} /> : <IconInfoCircle size={16} />}
        color={isSecure ? "green" : "yellow"}
        variant="light"
      >
        <Text size="sm">
          {isSecure ? (
            <>
              <strong>FTPS</strong> provides encrypted file transfer over
              TLS/SSL. Ensure your FTP server supports secure connections.
            </>
          ) : (
            <>
              <strong>Warning:</strong> FTP transfers files without encryption.
              Use FTPS for sensitive data.
            </>
          )}
        </Text>
      </Alert>

      {/* Host */}
      <TextInput
        label="Host"
        placeholder="ftp.example.com"
        description={`${protocol} server hostname or IP address`}
        value={config.host}
        onChange={handleHostChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Port */}
      <NumberInput
        label="Port"
        placeholder="21"
        description={`${protocol} server port (default: ${
          isSecure && config.secureImplicit ? "990" : "21"
        })`}
        value={config.port || 21}
        onChange={handlePortChange}
        disabled={disabled}
        min={1}
        max={65535}
        size="md"
      />

      {/* Username */}
      <TextInput
        label="Username"
        placeholder="ftp_user"
        description={`${protocol} login username`}
        value={config.username}
        onChange={handleUsernameChange}
        disabled={disabled}
        required
        size="md"
        autoComplete="off"
      />

      {/* Password */}
      <TextInput
        label="Password"
        placeholder="••••••••"
        description={`${protocol} login password`}
        value={config.password}
        onChange={handlePasswordChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Path Prefix */}
      <TextInput
        label="Remote Directory"
        placeholder="/uploads/{{formName}}/{{date}}"
        description="Directory path where files will be uploaded. Supports placeholders: {{formId}}, {{formName}}, {{date}}, {{year}}, {{month}}, {{day}}"
        value={config.pathPrefix || ""}
        onChange={handlePathPrefixChange}
        disabled={disabled}
        size="md"
      />

      {/* File Name Pattern */}
      <TextInput
        label="JSON File Name Pattern"
        placeholder="{{requestId}}.json"
        description="File name pattern for submission data JSON files. Supports placeholders: {{requestId}}, {{timestamp}}, plus all directory placeholders"
        value={config.fileNamePattern || "{{requestId}}.json"}
        onChange={handleFileNamePatternChange}
        disabled={disabled}
        size="md"
      />

      {/* FTPS-specific options */}
      {isSecure && (
        <>
          <Group justify="space-between" align="center">
            <div>
              <Text size="sm" fw={500}>
                Verify Server Certificate
              </Text>
              <Text size="xs" c="dimmed">
                Disable for self-signed certificates (less secure)
              </Text>
            </div>
            <Switch
              checked={config.rejectUnauthorized !== false}
              onChange={handleRejectUnauthorizedChange}
              disabled={disabled}
              size="md"
            />
          </Group>

          <Group justify="space-between" align="center">
            <div>
              <Text size="sm" fw={500}>
                Implicit FTPS Mode
              </Text>
              <Text size="xs" c="dimmed">
                Use implicit TLS (port 990) instead of explicit STARTTLS (port
                21)
              </Text>
            </div>
            <Switch
              checked={config.secureImplicit === true}
              onChange={handleSecureImplicitChange}
              disabled={disabled}
              size="md"
            />
          </Group>
        </>
      )}

      {/* Storage Path Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Example File Path:
        </Text>
        <Text size="xs">
          <code>{buildExamplePath(config)}</code>
        </Text>
        <Text size="xs" c="dimmed" mt={4}>
          Uploaded files are prefixed with the submission request ID to
          associate them with their submission.
        </Text>
      </Alert>
    </Stack>
  );
};

/**
 * FTP (unencrypted) connector configuration editor
 */
export const FtpConnectorConfigEditor: React.FC<
  Omit<FtpConnectorConfigEditorProps, "isSecure">
> = (props) => {
  return <FtpConnectorConfigEditorBase {...props} isSecure={false} />;
};

/**
 * FTPS (encrypted) connector configuration editor
 */
export const FtpsConnectorConfigEditor: React.FC<
  Omit<FtpConnectorConfigEditorProps, "isSecure">
> = (props) => {
  return <FtpConnectorConfigEditorBase {...props} isSecure={true} />;
};
